import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { characters } from '../../data/characters';
import { useVideoGeneration } from '../../hooks/useVideoGeneration';

interface OrderReviewProps {
  video: File | null;
  character: string | null;
  onConfirm: () => Promise<void>;
}

export const OrderReview: React.FC<OrderReviewProps> = ({
  video,
  character,
  onConfirm,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedCharacter = characters.find((c) => c.id === character);

  const handleConfirm = async () => {
    if (!video || !selectedCharacter) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await onConfirm();
    } catch (err: any) {
      setError(err.message || 'Failed to create order');
      console.error('Order creation failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">
        Review Your Order 🎁
      </h3>
      <p className="text-gray-600 mb-6">
        Please review your selections before proceeding:
      </p>

      <div className="space-y-6">
        {/* Video Preview */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Uploaded Video</h4>
          {video && (
            <div className="flex items-center text-green-600">
              <Check className="w-5 h-5 mr-2" />
              <span>{video.name}</span>
            </div>
          )}
        </div>

        {/* Character Preview */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Selected Character</h4>
          {selectedCharacter && (
            <div className="flex items-center space-x-4">
              <img
                src={selectedCharacter.image}
                alt={selectedCharacter.name}
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div>
                <p className="font-medium text-gray-900">
                  {selectedCharacter.name}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <p>{error}</p>
          </div>
        )}

        {/* Confirmation Button */}
        <div className="text-center pt-6">
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={isSubmitting || !video || !character}
            className="w-full md:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Confirm and Proceed'
            )}
          </Button>
          <p className="mt-4 text-sm text-gray-500">
            By proceeding, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};